import { Badge, Divider, Group, HoverCard, Stack, Text } from '@mantine/core';
import { IconChevronDown, IconClock, IconUser } from '@tabler/icons-react';
import { iconProps } from 'components/icon-props';
import { i18nKeys, useTranslation } from 'locales';
import { useThreadMessage } from './useThreadMessage';

export const ThreadMessageHeadersHover = () => {
  const { t } = useTranslation();
  const { mail, dateLabel } = useThreadMessage();

  const recipientLabel = mail.toMail.join(', ');
  const ccEmails = mail.cc ?? [];
  const bccEmails = mail.bcc ?? [];

  return (
    <HoverCard width={420} shadow="md" position="bottom-start" withinPortal>
      <HoverCard.Target>
        <Group
          gap="space2"
          wrap="nowrap"
          style={{ minWidth: 0 }}
          onClick={event => event.stopPropagation()}
        >
          <Text size="xs" c="dimmed" ff="monospace" truncate>
            {mail.fromMail} → {recipientLabel}
          </Text>
          {ccEmails.length > 0 && (
            <Badge size="xs" variant="default" color="gray" tt="none" style={{ flexShrink: 0 }}>
              {t(i18nKeys.CC)} {ccEmails.length}
            </Badge>
          )}
          {bccEmails.length > 0 && (
            <Badge size="xs" variant="default" color="gray" tt="none" style={{ flexShrink: 0 }}>
              {t(i18nKeys.BCC)} {bccEmails.length}
            </Badge>
          )}
          <IconChevronDown
            {...iconProps(10)}
            color="var(--mantine-color-dimmed)"
            style={{ flexShrink: 0 }}
          />
        </Group>
      </HoverCard.Target>
      <HoverCard.Dropdown p="space4" onClick={event => event.stopPropagation()}>
        <Group justify="space-between" gap="space3">
          <Group gap={6}>
            <IconUser {...iconProps(12)} color="var(--mantine-color-dimmed)" />
            <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts={0.5}>
              {t(i18nKeys.EXTERNAL_MAIL.DETAIL_NEXT.HEADERS_LABEL)}
            </Text>
          </Group>
          <Group gap={6}>
            <IconClock {...iconProps(12)} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">
              {dateLabel}
            </Text>
          </Group>
        </Group>
        <Divider my="space3" />
        <Stack gap="space2">
          <HeaderRow label={t(i18nKeys.FORM.FROM)} emails={[mail.fromMail]} />
          <HeaderRow label={t(i18nKeys.FORM.TO)} emails={mail.toMail} />
          {ccEmails.length > 0 && <HeaderRow label={t(i18nKeys.CC)} emails={ccEmails} />}
          {bccEmails.length > 0 && <HeaderRow label={t(i18nKeys.BCC)} emails={bccEmails} />}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

type HeaderRowProps = {
  label: string;
  emails: ReadonlyArray<string>;
};

const HeaderRow = ({ label, emails }: HeaderRowProps) => (
  <Group gap="space3" align="flex-start" wrap="nowrap">
    <Text size="xs" tt="uppercase" fw={600} c="dimmed" lts={0.5} w={36} style={{ flexShrink: 0 }}>
      {label}
    </Text>
    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
      {emails.map(email => (
        <Text key={email} size="xs" c="gray.7">
          {email}
        </Text>
      ))}
    </Stack>
  </Group>
);
